// src/context/ThemeContext.jsx
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { getTheme } from "../theme/theme";
import { useLocation } from "react-router-dom";

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeProvider");
  }
  return context;
};

// Define routes that should support dark mode
const THEME_ENABLED_ROUTES = [
  "/dashboard",
  "/interviews",
  "/positions",
  "/user-management",
  "/candidates",
  "/email-activity",
  "/view-recording",
];

// Check if current path matches any theme-enabled routes
const isThemeEnabledRoute = (pathname) => {
  return THEME_ENABLED_ROUTES.some((route) => pathname.startsWith(route));
};

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [userPreference, setUserPreference] = useState(() => {
    const savedMode = localStorage.getItem("theme-mode");
    return savedMode || "light";
  });

  // Determine actual mode based on route and user preference
  const mode = useMemo(() => {
    // If not on a theme-enabled route, force light mode
    if (!isThemeEnabledRoute(location.pathname)) {
      return "light";
    }
    // Otherwise, use user preference
    return userPreference;
  }, [location.pathname, userPreference]);

  // Check if theme toggle should be available
  const canToggleTheme = useMemo(() => {
    return isThemeEnabledRoute(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("theme-mode", userPreference);
  }, [userPreference]);

  const toggleTheme = () => {
    // Only allow toggle on theme-enabled routes
    if (canToggleTheme) {
      setUserPreference((prevMode) =>
        prevMode === "light" ? "dark" : "light"
      );
    }
  };

  const theme = useMemo(() => getTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      toggleTheme,
      canToggleTheme, // Export this so components know if toggle should be shown
    }),
    [mode, canToggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
